<template>
  <div class="milestone" :class="[cssType, locale]">
    <div class="section-head" :class="{ 'has-design-title': designTitle }">
      <img v-if="designTitle" class="design-title" :src="designTitle" :alt="$t('novShopping.milestone.subtitle')" />
      <template v-else>
        <img class="part-watermark" :src="partWatermarkImg" alt="" />
        <div class="title-row">
          <img class="gift" :src="giftImg" alt="" />
          <img v-if="subtitleImg" class="milestone-title" :class="locale" :src="subtitleImg" alt="title" />
        </div>
      </template>
    </div>

    <div class="actions">
      <div class="act-btn act-btn--blue" @click="$emit('top-up')">
        {{ $t('novShopping.milestone.topUp') }}
      </div>
      <div class="act-btn act-btn--blue" @click="$emit('open-record')">
        {{ $t('novShopping.milestone.history') }}
      </div>
      <div class="act-btn act-btn--yellow" :class="{ 'is-disabled': !canClaimAll }" @click="handleClaimAll">
        {{ $t('novShopping.milestone.claimAll') }}
      </div>
    </div>

    <div class="milestone-panel">
      <div class="panel-head" v-if="nextTip || props.list.length">{{ nextTip || $t('novShopping.milestone.allAchieved') }}</div>

      <!-- PC：五列卡片 + 横向进度条 -->
      <template v-if="isPC">
        <div class="milestone-list">
          <MilestoneCard
            v-for="item in props.list"
            :key="item.rewardCode"
            :item="item"
            :activity-phase="props.activityPhase"
            @claim="handleClaim(item)"
          />
        </div>
        <MilestoneProgress
          :list="props.list"
          :nodes="nodePercents"
          :percent="fillPercent"
        />
      </template>

      <!-- Mobile：垂直时间轴，卡片即节点 -->
      <MilestoneTimeline
        v-else
        :list="props.list"
        :percent="fillPercent"
        :activity-phase="props.activityPhase"
        @claim="handleClaim"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { isMobile } from '@/utils'
import MilestoneCard from './components/MilestoneCard/index.vue'
import MilestoneProgress from './components/MilestoneProgress/index.vue'
import MilestoneTimeline from './components/MilestoneTimeline/index.vue'
import pcSubtitleImg from '../../assets/imgs/milestone/mainTitle/index.js'
import mobileSubtitleImg from '../../assets/imgs/milestone/mobile/mainTitle/index.js'
import partWatermarkImg from '../../assets/imgs/milestone/part3-watermark.png'
import { milestoneTitle as designTitle } from '../../assets/imgs/sectionTitles'
import giftImg from '../../assets/imgs/goods/gift.png'

const props = defineProps({
  activityPhase: { type: String, default: 'loading' },
  // [{ rewardCode, targetSpent, props: [{propId, propName, propCount}], status }]
  // status: 0-未达成 1-可领取 2-已领取
  list: {
    type: Array,
    default: () => [],
  },
  voucherSpent: {
    type: Number,
    default: 0,
  },
  loggedIn: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['claim', 'claim-all', 'open-record', 'top-up', 'login-required'])

const { t, locale } = useI18n()
const cssType = isMobile ? 'mobile' : 'pc'
const isPC = cssType === 'pc'

// PC 与移动端副标题艺术字版式不同（PC 单行、Mobile 两行），分别取图
const subtitleImg = computed(() => (isPC ? pcSubtitleImg.value : mobileSubtitleImg.value))

const canClaimAll = computed(() => props.activityPhase === 'active' && props.list.some(item => item.status === 1))

const nodePercents = computed(() => {
  const total = props.list.length
  // 设计稿：首档贴在进度条左端、末档贴在右端，中间等距
  if (total <= 1) return total ? [50] : []
  return props.list.map((item, idx) => (idx / (total - 1)) * 100)
})

/**
 * 节点等距排布，进度条按「已达成的节点」分段填充，
 * 未达成的区段按当前消耗在该档区间内的比例插值。
 * PC 横向进度条与 Mobile 竖向轨道共用该结果。
 */
const fillPercent = computed(() => {
  const list = props.list
  if (!list.length) return 0
  const spent = Number(props.voucherSpent) || 0
  const last = Number(list[list.length - 1].targetSpent) || 0
  if (last > 0 && spent >= last) return 100

  const idx = list.findIndex(item => spent < Number(item.targetSpent))
  if (idx < 0) return 100

  const lowerTarget = idx === 0 ? 0 : Number(list[idx - 1].targetSpent)
  const lowerPos = idx === 0 ? 0 : nodePercents.value[idx - 1]
  const upperPos = nodePercents.value[idx]
  const upperTarget = Number(list[idx].targetSpent)
  if (upperTarget <= lowerTarget) return lowerPos

  const ratio = Math.min(1, Math.max(0, (spent - lowerTarget) / (upperTarget - lowerTarget)))
  return lowerPos + (upperPos - lowerPos) * ratio
})

const nextTip = computed(() => {
  // 提示只针对「未达成」的下一个节点；已可领取/已领取不参与
  const next = props.list.find(item => item.status === 0)
  if (!next) return ''
  const target = Number(next.targetSpent) || 0
  if (!props.loggedIn) {
    return t('novShopping.milestone.nextTip', { n: target })
  }
  const remain = Math.max(0, target - (Number(props.voucherSpent) || 0))
  return t('novShopping.milestone.nextTip', { n: remain })
})

const handleClaim = (item) => {
  if (props.activityPhase !== 'active' || item.status !== 1) return
  if (!props.loggedIn) {
    emit('login-required')
    return
  }
  emit('claim', item.rewardCode)
}

const handleClaimAll = () => {
  if (!canClaimAll.value) return
  if (!props.loggedIn) {
    emit('login-required')
    return
  }
  emit('claim-all')
}
</script>

<style lang="less" scoped src="./pc_style.less"></style>
<style lang="less" scoped src="./mobile_style.less"></style>
